import { useMutation } from '@tanstack/react-query';
import { FORCE_FORBIDDEN_HEADER } from '@sentinel/api-client';
import { useApi } from '../../lib/api';
import type { AuditLogFilters } from './use-audit-log';

export interface AuditExportVars extends AuditLogFilters {
  format: 'csv' | 'jsonl';
}

export interface AuditExportResult {
  /** Filename used for the download, useful for tests and a11y status. */
  filename: string;
}

function isoDate(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10);
}

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const m = /filename="?([^"]+)"?/i.exec(header);
  return m?.[1] ?? fallback;
}

/**
 * Calls `GET /auth/account/audit-log/export` and triggers a CSV or JSONL
 * download. Uses `parseAs: 'blob'` so the openapi-fetch middleware still runs
 * onResponse. Optional query params are conditionally spread so absent filters
 * are OMITTED rather than passed as `undefined` (required by
 * `exactOptionalPropertyTypes`).
 */
export function useAuditExport() {
  const api = useApi();
  return useMutation<AuditExportResult, Error, AuditExportVars>({
    mutationFn: async (vars) => {
      const query = {
        format: vars.format as string,
        ...(vars.eventTypes.length ? { event_types: vars.eventTypes.join(',') } : {}),
        ...(vars.dateFrom != null ? { date_from: vars.dateFrom } : {}),
        ...(vars.dateTo != null ? { date_to: vars.dateTo } : {}),
      };

      const { data, error, response } = await api.GET('/auth/account/audit-log/export', {
        params: { query },
        parseAs: 'blob',
        // A user-initiated action that happens to be a GET, so it opts in to
        // the global forbidden handler the mutation-only rule would skip.
        headers: { [FORCE_FORBIDDEN_HEADER]: '1' },
      });

      if (error || !data) throw new Error('audit_export_failed');

      const fallback = `audit-log-${isoDate()}.${vars.format}`;
      const filename = filenameFromDisposition(
        response?.headers.get('content-disposition') ?? null,
        fallback,
      );

      const url = URL.createObjectURL(data as Blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        // Defer revocation to ensure the click had a chance to start the
        // download in all browsers.
        setTimeout(() => URL.revokeObjectURL(url), 0);
      }

      return { filename };
    },
  });
}
